import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { INR, photoUrl } from "@/lib/api";
import Navbar from "@/components/app/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MapPin, BedDouble, Users, ShieldCheck, Flag, Home, Zap, Calendar, Wifi, Bath, Wind } from "lucide-react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";

// Fix for leaflet default marker icon
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [enqMsg, setEnqMsg] = useState("Hi! Is this still available?");
  const [reportReason, setReportReason] = useState("broker");

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data.listing)).finally(() => setLoading(false));
  }, [id]);

  const sendEnquiry = async () => {
    if (!user) { nav("/login"); return; }
    try {
      const r = await api.post("/interests", { listing_id: id, message: enqMsg });
      toast.success(r.data.duplicate ? "Enquiry already sent" : "Enquiry sent!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  const report = async () => {
    if (!user) { nav("/login"); return; }
    try {
      await api.post("/reports", { target_type: "listing", target_id: id, reason: reportReason });
      toast.success("Reported. Our team will review.");
    } catch { toast.error("Could not report"); }
  };

  if (loading) return <><Navbar /><div className="max-w-6xl mx-auto p-8 text-stone-500">Loading…</div></>;
  if (!listing) return <><Navbar /><div className="max-w-6xl mx-auto p-8">Listing not found</div></>;

  const cover = listing.photos?.[active];
  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button data-testid="back-btn" onClick={() => nav(-1)} className="text-sm text-stone-500 mb-4">← Back</button>

        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-stone-200 bg-white">
                  {cover && <img loading="lazy" src={photoUrl(cover)} alt="" className="w-full aspect-[4/3] object-cover" />}
            {listing.photos?.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {listing.photos.map((p, i) => (
                  <button key={p} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                    className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border ${i === active ? "border-[#2A4B3C]" : "border-stone-200"}`}>
                    <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="p-6 bg-white border border-stone-200 rounded-2xl">
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <MapPin className="w-4 h-4" /> {listing.locality}, {listing.city}
                {listing.boosted && <span className="chip bg-amber-100 text-amber-800 flex items-center gap-1 ml-auto"><Zap className="w-3 h-3" /> Boosted</span>}
              </div>
              <h1 className="font-display text-2xl font-medium text-stone-900 mt-2 leading-tight">{listing.title}</h1>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-display text-3xl font-semibold text-brand">{INR(listing.rent)}</div>
                <div className="text-stone-500 text-sm">{listing.units?.length > 0 ? "/ mo onwards" : "/ month"}</div>
              </div>
              {listing.units?.length === 0 && <div className="text-xs text-stone-500 mt-1">Deposit: {INR(listing.deposit)}</div>}

              <div className="mt-4 flex flex-wrap gap-2">
                <span className="chip flex items-center gap-1"><BedDouble className="w-3 h-3" /> {listing.bhk}</span>
                <span className="chip capitalize">{listing.furnishing}</span>
                <span className="chip capitalize">{listing.room_type}</span>
                <span className="chip">{listing.listing_category}</span>
                {listing.preferences?.gender && listing.preferences.gender !== "any" && <span className="chip capitalize">{listing.preferences.gender} pref</span>}
                {listing.preferences?.food_pref === "veg" && <span className="chip">Veg</span>}
                {listing.preferences?.pets_allowed && <span className="chip">Pets ok</span>}
              </div>

              <div className="mt-4 flex items-center gap-2 text-sm text-stone-600">
                <Calendar className="w-4 h-4" /> Available from {new Date(listing.available_from).toLocaleDateString()}
              </div>

              <Dialog>
                <DialogTrigger asChild>
                  <Button data-testid="enquire-btn" className="brand-accent w-full rounded-full mt-6 h-11">
                    Send enquiry
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Enquire about "{listing.title}"</DialogTitle></DialogHeader>
                  <Textarea data-testid="enquiry-message" value={enqMsg} onChange={(e) => setEnqMsg(e.target.value)} rows={4} />
                  <p className="text-xs text-stone-500">Chat opens once the owner accepts. Your contact info stays private.</p>
                  <DialogFooter>
                    <Button data-testid="enquiry-send" className="brand-accent rounded-full" onClick={sendEnquiry}>Send</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <div className="mt-4 pt-4 border-t border-stone-200 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500">Listed by</div>
                  <button className="text-sm font-medium text-stone-900" onClick={() => nav(`/user/${listing.owner_id}`)}>{listing.owner_name}</button>
                </div>
                {listing.owner_verified && (
                  <span className="chip chip-verified"><ShieldCheck className="w-3 h-3" /> Verified</span>
                )}
              </div>
            </div>

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" data-testid="report-btn" className="w-full text-stone-500 hover:text-red-600">
                  <Flag className="w-4 h-4 mr-2" /> Report listing
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Report this listing</DialogTitle></DialogHeader>
                <select data-testid="report-reason" className="w-full border border-stone-200 rounded-xl px-3 py-2" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                  <option value="broker">Looks like a broker</option>
                  <option value="fake">Fake or inaccurate</option>
                  <option value="scam">Possible scam</option>
                  <option value="offensive">Offensive content</option>
                </select>
                <DialogFooter>
                  <Button data-testid="report-submit" className="brand-primary rounded-full" onClick={report}>Submit report</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Unit / Room category cards — for PG / Hostel / Shared Flat */}
        {listing.units?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-xl font-medium mb-4">Room categories</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {listing.units.map((unit, i) => (
                <div key={i} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                  {/* Unit photos */}
                  {unit.photos?.length > 0 && (
                    <img
                      src={photoUrl(unit.photos[0])}
                      alt={unit.title || `Room ${i + 1}`}
                      className="w-full h-40 object-cover"
                    />
                  )}
                  <div className="p-4">
                    <div className="font-semibold text-stone-800 mb-1">{unit.title || `Room category ${i + 1}`}</div>
                    <div className="font-display text-xl font-semibold text-brand">
                      {INR(unit.rent)}<span className="text-sm font-normal text-stone-500">/mo</span>
                    </div>
                    <div className="text-xs text-stone-500 mb-3">Deposit: {INR(unit.deposit)}</div>
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      {unit.occupancy > 1 && (
                        <span className="chip flex items-center gap-1"><Users className="w-3 h-3" /> {unit.occupancy} sharing</span>
                      )}
                      {unit.bathroom_type && (
                        <span className="chip flex items-center gap-1"><Bath className="w-3 h-3" /> {unit.bathroom_type}</span>
                      )}
                      {unit.electricity_included && (
                        <span className="chip">Elec. included</span>
                      )}
                      {unit.units_available > 0 && (
                        <span className="chip">{unit.units_available} available</span>
                      )}
                    </div>
                    {unit.amenities?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {unit.amenities.map((a) => (
                          <span key={a} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{a}</span>
                        ))}
                      </div>
                    )}
                    {unit.available_from && (
                      <div className="mt-2 text-xs text-stone-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> From {new Date(unit.available_from).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* About */}
        <section className="mt-10 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 p-6 bg-white border border-stone-200 rounded-2xl">
            <h2 className="font-display text-xl font-medium mb-3">About this place</h2>
            <p className="text-stone-600 leading-relaxed whitespace-pre-line">{listing.description}</p>

            {listing.amenities?.length > 0 && (
              <>
                <h3 className="font-display text-lg font-medium mt-8 mb-2">
                  {listing.units?.length > 0 ? "Common facilities" : "Amenities"}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {listing.amenities.map((a) => (
                    <span key={a} className="chip"><Home className="w-3 h-3 mr-1" /> {a}</span>
                  ))}
                </div>
              </>
            )}

            {listing.house_rules?.length > 0 && (
              <>
                <h3 className="font-display text-lg font-medium mt-8 mb-2">House rules</h3>
                <ul className="text-stone-600 space-y-1 list-disc list-inside">
                  {listing.house_rules.map((r) => <li key={r}>{r}</li>)}
                </ul>
              </>
            )}

            {listing.latitude && listing.longitude && (
              <>
                <h3 className="font-display text-lg font-medium mt-8 mb-2">Location</h3>
                <div className="w-full h-[280px] rounded-xl overflow-hidden border border-stone-200 mt-2 z-10 relative">
                  <MapContainer
                    center={[listing.latitude, listing.longitude]}
                    zoom={15}
                    scrollWheelZoom={false}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[listing.latitude, listing.longitude]} />
                  </MapContainer>
                </div>
              </>
            )}
          </div>

          <div className="p-6 bg-white border border-stone-200 rounded-2xl">
            <h2 className="font-display text-xl font-medium mb-3">Quick facts</h2>
            <dl className="space-y-2 text-sm">
              <Row k="Category" v={listing.listing_category} />
              <Row k="Occupation pref" v={listing.preferences?.occupation || "any"} />
              <Row k="Smoking" v={listing.preferences?.smoking_allowed ? "Allowed" : "Not allowed"} />
              <Row k="Views" v={String(listing.views || 0)} />
              <Row k="Enquiries" v={String(listing.enquiries_count || 0)} />
            </dl>
          </div>
        </section>
      </div>
    </div>
  );
}
const Row = ({ k, v }) => (
  <div className="flex justify-between py-1 border-b border-stone-100 last:border-0"><dt className="text-stone-500">{k}</dt><dd className="text-stone-800 capitalize">{v}</dd></div>
);
